// src/pages/UserDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/api";
import "../css/userdeatil.css";

export function UserDetail({ onClose }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

const [pw, setPw] = useState({ new1: "", new2: "", show: false });
const [pwSaving, setPwSaving] = useState(false);


  // 역할/저장 상태
  const [saving, setSaving] = useState(false);

  // ✨ 프로필 편집 상태(이름/부서/이메일/휴대폰)
  const [profile, setProfile] = useState({
    username:"",
    name: "",
    department: "",
    email: "",
    phone: "",
  });
  const [pSaving, setPSaving] = useState(false);

  // 위치 접근 제어 상태
  const [allLocations, setAllLocations] = useState([]);
  const [grantedKeys, setGrantedKeys] = useState(new Set());
  const [locLoading, setLocLoading] = useState(true);
  const [locError, setLocError] = useState(null);
  const [locSaving, setLocSaving] = useState(false);
  const [locQuery, setLocQuery] = useState("");

  // 토큰 없으면 로그인으로
  useEffect(() => {
    const t = localStorage.getItem("accessToken");
    if (!t) {
      alert("로그인이 필요합니다.");
      navigate("/login");
    }
  }, [navigate]);

  // 역할 옵션
  const roleOptions = ["USER", "NEW", "ADMIN"];

  const pickLocKey = (l) => l?.id ?? l?.name ?? l;
  const pickLocLabel = (l) => l?.name ?? String(l);

  const load = useCallback(
    async (signal) => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/users/${encodeURIComponent(id)}`, { signal });
        const u = res?.data ?? null;
        setUser(u);

        // ✨ 불러온 값으로 프로필 입력 초기화
        setProfile({
          username:u?.username ??"",
          name: u?.name ?? "",
          department: u?.department ?? "",
          email: u?.email ?? "",
          phone: u?.phone ?? "",
        });
      } catch (err) {
        if (err?.name === "CanceledError" || err?.message === "canceled") return;
        setError(err?.response?.data?.message || err?.message || "상세 정보를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const savePassword = async () => {
  const p1 = pw.new1, p2 = pw.new2;
  if (!p1 || !p2) { alert("새 비밀번호를 입력하세요."); return; }
  if (p1 !== p2) { alert("비밀번호 확인이 일치하지 않습니다."); return; }
  if (p1.length < 8) { alert("비밀번호는 최소 8자 이상을 권장합니다."); return; }

  try {
    setPwSaving(true);
    // 권장: 별도 엔드포인트로 처리
    await api.patch(`/admin/users/${encodeURIComponent(id)}/password`, { newPassword: p1 });
    setPw({ new1: "", new2: "", show: false });
    alert("비밀번호가 변경되었습니다.\n보안을 위해 기존 세션/토큰이 만료될 수 있습니다.");
  } catch (err) {
    // 만약 서버가 PATCH /admin/users 로 password를 받도록 되어 있다면, 위 라인을 교체하세요.
    alert(`비밀번호 변경 실패: ${err?.response?.data?.message || err?.message}`);
  } finally { setPwSaving(false); }
};

  const loadLocations = useCallback(
    async (signal) => {
      if (!id) return;
      try {
        setLocLoading(true);
        setLocError(null);
        const [allRes, grantedRes] = await Promise.all([
          api.get("/locations", { signal }),
          api.get(`/admin/users/${encodeURIComponent(id)}/locations`, { signal }),
        ]);
        const all = Array.isArray(allRes?.data) ? allRes.data : [];
        const grantedArr = Array.isArray(grantedRes?.data) ? grantedRes.data : [];
        setAllLocations(all);
        setGrantedKeys(new Set(grantedArr.map(pickLocKey)));
      } catch (err) {
        if (err?.name === "CanceledError" || err?.message === "canceled") return;
        setLocError(err?.response?.data?.message || err?.message || "위치 목록을 불러오지 못했습니다.");
      } finally {
        setLocLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    loadLocations(controller.signal);
    return () => controller.abort();
  }, [load, loadLocations]);

  const currentRole = (() => {
    if (!user) return "";
    if (user.role) return user.role;
    if (Array.isArray(user.roles) && user.roles.length) return user.roles[0];
    return "";
  })();

  const onChangeRole = async (nextRole) => {
    try {
      setSaving(true);
      await api.post(`/admin/users/${encodeURIComponent(id)}/role`, null, {
        params: { role: nextRole },
      });
      setUser((prev) => ({ ...(prev ?? {}), role: nextRole }));
      alert("역할이 변경되었습니다.");
    } catch (err) {
      alert(`변경 실패: ${err?.response?.data?.message || err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ✨ 프로필 저장
  const saveProfile = async () => {

    const uname = (profile.username || "").trim();
    if (!uname) { alert("username을 입력하세요."); return; }

    // 간단 검증
    if (profile.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(profile.email)) {
      alert("이메일 형식을 확인해주세요.");
      return;
    }
    // 전화번호는 숫자/하이픈만 허용(백엔드에서 더 강한 검증 가능)
    const phoneSanitized = String(profile.phone || "").replace(/[^\d\-+]/g, "");

    try {
      setPSaving(true);
      await api.patch(`/admin/users/${encodeURIComponent(id)}`, {
        username: uname,
        name: profile.name?.trim() || "",
        department: profile.department?.trim() || "",
        email: profile.email?.trim() || "",
        phone: phoneSanitized,
      });
      setUser((u) => ({
        ...(u || {}),
        name: profile.name,
        department: profile.department,
        email: profile.email,
        phone: phoneSanitized,
      }));
      alert("프로필이 저장되었습니다.");
    } catch (err) {
      alert(`프로필 저장 실패: ${err?.response?.data?.message || err?.message}`);
    } finally {
      setPSaving(false);
    }
  };

  const toggleLoc = (key) => {
    setGrantedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const saveLocations = async () => {
    try {
      const names = Array.from(grantedKeys);
      setLocSaving(true);
      await api.post(`/admin/users/${encodeURIComponent(id)}/locations`, names);
      alert("허용 위치가 저장되었습니다.");
    } catch (err) {
      alert(`저장 실패: ${err?.response?.data?.message || err?.message}`);
    } finally {
      setLocSaving(false);
    }
  };

  const filteredLocations = useMemo(() => {
    const q = locQuery.trim().toLowerCase();
    const items = allLocations.map((l) => ({ key: pickLocKey(l), label: pickLocLabel(l) }));
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [allLocations, locQuery]);

  return (
    <div className="udetail">
      <div className="udetail__header">
        <h2 className="udetail__title">사용자 상세</h2>
        <div className="udetail__actions">
          <button onClick={() => navigate(-1)} className="btn">뒤로</button>
          {onClose && <button onClick={() => onClose()} className="btn">닫기</button>}
        </div>
      </div>

      <div className="card card--pad">
        {loading ? (
          <div className="status status--muted animate-pulse">불러오는 중…</div>
        ) : error ? (
          <div className="status status--error">오류: {error}</div>
        ) : !user ? (
          <div className="status status--muted">사용자 정보를 찾을 수 없습니다.</div>
        ) : (
          <>
            {/* ✨ 기본 정보(편집 가능) */}
            <div className="section">
              <div className="info-grid">
                <Info label="사용자ID" value={normalizeId(user)} />
                <div className="info">
               <label className="form-label">username</label>
          <input
        className="input"
        value={profile.username}
        onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))}
        placeholder="로그인 아이디"
        autoComplete="username"
      />
    </div>


                <div className="info">
                  <label className="form-label">이름</label>
                  <input
                    className="input"
                    value={profile.name}
                    onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                    placeholder="홍길동"
                  />
                </div>
                <div className="info">
                  <label className="form-label">부서</label>
                  <input
                    className="input"
                    value={profile.department}
                    onChange={(e) => setProfile((p) => ({ ...p, department: e.target.value }))}
                    placeholder="생산관리팀"
                  />
                </div>
                <div className="info">
                  <label className="form-label">이메일</label>
                  <input
                    className="input"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="info">
                  <label className="form-label">휴대폰</label>
                  <input
                    className="input"
                    value={profile.phone}
                    onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="010-1234-5678"
                  />
                </div>
              </div>
              <div className="row" style={{ marginTop: 10 }}>
                <button className="btn btn--primary" onClick={saveProfile} disabled={pSaving}>
                  {pSaving ? "저장 중…" : "기본 정보 저장"}
                </button>
              </div>
            </div>

            
  {/* 비밀번호 변경 */}
  <div className="row" style={{ marginTop: 16, gap: 8 }}>
    <div className="info" style={{ flex: 1 }}>
      <label className="form-label">새 비밀번호</label>
      <input
        className="input"
        type={pw.show ? "text" : "password"}
        value={pw.new1}
        onChange={(e) => setPw(s => ({ ...s, new1: e.target.value }))}
        placeholder="새 비밀번호"
        autoComplete="new-password"
      />
    </div>
    <div className="info" style={{ flex: 1 }}>
      <label className="form-label">새 비밀번호 확인</label>
      <input
        className="input"
        type={pw.show ? "text" : "password"}
        value={pw.new2}
        onChange={(e) => setPw(s => ({ ...s, new2: e.target.value }))}
        placeholder="다시 입력"
        autoComplete="new-password"
      />
    </div>
  </div>
  <div className="row" style={{ marginTop: 8, gap: 8, alignItems: "center" }}>
    <label className="checkbox">
      <input
        type="checkbox"
        checked={pw.show}
        onChange={(e) => setPw(s => ({ ...s, show: e.target.checked }))}
      />
      <span className="caption">비밀번호 표시</span>
    </label>
    <button className="btn btn--danger" onClick={savePassword} disabled={pwSaving}>
      {pwSaving ? "변경 중…" : "비밀번호 변경"}
    </button>
  </div>

            {/* 역할 변경 */}
            <div className="section">
              <label className="form-label">역할(Role)</label>
              <div className="row">
                <select
                  className="select"
                  value={currentRole}
                  onChange={(e) => onChangeRole(e.target.value)}
                  disabled={saving}
                >
                  <option value="" disabled>선택하세요</option>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {saving && <span className="caption">저장 중…</span>}
              </div>
            </div>

            {/* 허용 위치 관리 */}
            <div className="section">
              <div className="row row--spread">
                <label className="form-label">허용 위치</label>
                <div className="row">
                  <input
                    type="text"
                    className="input input--dense"
                    placeholder="위치 검색"
                    value={locQuery}
                    onChange={(e) => setLocQuery(e.target.value)}
                  />
                  <button
                    className="btn btn--dense"
                    onClick={saveLocations}
                    disabled={locSaving || locLoading}
                  >
                    {locSaving ? "저장 중…" : "저장"}
                  </button>
                </div>
              </div>

              {locLoading ? (
                <div className="status status--muted animate-pulse">위치 목록 불러오는 중…</div>
              ) : locError ? (
                <div className="status status--error">오류: {locError}</div>
              ) : (
                <div className="listbox">
                  {filteredLocations.length === 0 ? (
                    <div className="status status--muted">검색 결과가 없습니다.</div>
                  ) : (
                    <ul className="vstack vstack--tight">
                      {filteredLocations.map(({ key, label }) => (
                        <li key={key} className="listitem">
                          <input
                            id={`loc-${key}`}
                            type="checkbox"
                            className="chk"
                            checked={grantedKeys.has(key)}
                            onChange={() => toggleLoc(key)}
                          />
                          <label htmlFor={`loc-${key}`} className="listitem__label">
                            {label}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="caption mt-2">
                선택: {grantedKeys.size} / 전체: {allLocations.length}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function normalizeId(u) {
  return u?.id ?? u?.userId ?? u?.username ?? u?.email;
}

function Info({ label, value }) {
  return (
    <div className="info">
      <span className="info__label">{label}</span>
      <span className="info__value">{value != null && value !== "" ? String(value) : "-"}</span>
    </div>
  );
}
